import React, { useState, useEffect } from "react";
import axios from "axios";
import Modal from "react-modal";
import "./OrderGifts.css"; // Custom CSS for styling
import { useAuth } from "../AuthContext";

Modal.setAppElement("#root"); // Set the app root element for accessibility

const OrderGifts = () => {
  const [giftOrders, setGiftOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const { mobile } = useAuth();
  const [newGiftOrder, setNewGiftOrder] = useState({
    partyName: "",
    employeeName: "",
    address: "",
    phoneNumber: "",
    pinCode: "",
    invoiceNumber: "",
    totalCredits: "",
    gifts: "",
    details: "",
    status: "Pending",
  });

  // State for password modal
  const [passwordModalIsOpen, setPasswordModalIsOpen] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedOrderStatus, setSelectedOrderStatus] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchGiftOrders();
  }, []);

  const fetchGiftOrders = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/gift_orders`
      );
      setGiftOrders(
        response.data.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        )
      );
    } catch (error) {
      console.error("Failed to fetch gift orders:", error);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleOverlayClick = (e) => {
    // Check if the clicked element is not the close button
    if (e.target !== document.querySelector(".modal-header button")) {
      e.stopPropagation(); // Prevent default behavior (closing modal)
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewGiftOrder({
      ...newGiftOrder,
      [name]: value,
    });
  };

  const handleAddGiftOrder = async (event) => {
    event.preventDefault();
    setLoading(true); // Disable the submit button
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/gift_orders`,
        newGiftOrder
      );
      setModalIsOpen(false);
      fetchGiftOrders();
      setNewGiftOrder({
        partyName: "",
        employeeName: "",
        address: "",
        phoneNumber: "",
        pinCode: "",
        invoiceNumber: "",
        totalCredits: "",
        gifts: "",
        details: "",
        status: "Pending",
      });
    } catch (error) {
      console.error("Failed to add gift order:", error);
    }
    setLoading(false);
  };

  const handleChangeOrderStatus = async (orderId, newStatus) => {
    // Store selected order details to update later
    setSelectedOrderId(orderId);
    setSelectedOrderStatus(newStatus);

    // Open password modal
    setPasswordModalIsOpen(true);
  };

  const handlePasswordInputChange = (e) => {
    setPasswordInput(e.target.value);
  };

  const handlePasswordSubmit = async () => {
    // Close password modal
    setPasswordModalIsOpen(false);

    // Check if password matches
    if (passwordInput === "2804") {
      try {
        const response = await axios.patch(
          `${process.env.REACT_APP_API_URL}/api/gift_orders/${selectedOrderId}`,
          { status: selectedOrderStatus }
        );
        fetchGiftOrders();
      } catch (error) {
        console.error("Failed to change order status:", error);
      }
    } else {
      alert("Incorrect password. Status update aborted.");
    }

    // Reset password input
    setPasswordInput("");
  };

  return (
    <div className="order-gifts-container">
      <h1>Order Gifts</h1>
      <div className="search-bar-button-container">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>
        <button
          className="add-gift-order-btn"
          onClick={() => setModalIsOpen(true)}
        >
          Add Gift Order
        </button>
      </div>
      <table className="order-gifts-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Party Name</th>
            <th>Emp Name</th>
            <th>Address</th>
            {mobile === "9041677932" && <th>Mobile</th>}
            <th>Pin Code</th>
            <th>Invoice Number</th>
            <th>Total Credits</th>
            <th>Gifts</th>
            <th>Details</th>
            <th>Status</th>
            {mobile === "9041677932" && <th>Action</th>}
          </tr>
        </thead>
        <tbody>
          {giftOrders
            .filter((order) =>
              [
                order.party_name,
                order.employee_name,
                order.address,
                order.phone_number,
                order.pin_code,
                order.invoice_number,
                order.total_credits,
                order.gifts,
                order.details,
                order.status,
              ]
                .join(" ")
                .toLowerCase()
                .includes(searchTerm.toLowerCase())
            )
            .map((order) => (
              <tr
                key={order.id}
                className={order.status === "Done" ? "done-status" : ""}
              >
                <td>
                  {new Date(order.created_at).toLocaleDateString("en-GB")}
                </td>
                <td>{order.party_name}</td>
                <td>{order.employee_name}</td>
                <td style={{ whiteSpace: "pre-wrap" }}>{order.address}</td>
                {mobile === "9041677932" && <td>{order.phone_number}</td>}
                <td>{order.pin_code}</td>
                <td>{order.invoice_number}</td>
                <td>{order.total_credits}</td>
                <td style={{ whiteSpace: "pre-wrap" }}>{order.gifts}</td>
                <td style={{ whiteSpace: "pre-wrap" }}>{order.details}</td>
                <td>{order.status}</td>
                {mobile === "9041677932" && (
                  <th>
                    <button
                      onClick={() => handleChangeOrderStatus(order.id, "Done")} // Replace "New Status" with desired options
                    >
                      Change Status
                    </button>
                  </th>
                )}
              </tr>
            ))}
        </tbody>
      </table>

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={() => setModalIsOpen(false)}
        className="modal"
        overlayClassName="modal-overlay"
        contentLabel="Add New Gift Order"
        onOverlayClick={handleOverlayClick}
      >
        <div className="modal-header">
          <h2>Add New Gift Order</h2>
          <button onClick={() => setModalIsOpen(false)}>Close</button>
        </div>
        <div className="modal-body">
          <form onSubmit={handleAddGiftOrder}>
            <label>
              Party Name:
              <input
                type="text"
                name="partyName"
                value={newGiftOrder.partyName}
                onChange={handleInputChange}
                required
              />
            </label>
            <label>
              Employee Name:
              <input
                type="text"
                name="employeeName"
                value={newGiftOrder.employeeName}
                onChange={handleInputChange}
                required
              />
            </label>
            <label>
              Address:
              <textarea
                name="address"
                value={newGiftOrder.address}
                onChange={handleInputChange}
                required
                style={{ height: "80px" }}
              ></textarea>
            </label>
            <label>
              Phone Number:
              <input
                type="text"
                name="phoneNumber"
                value={newGiftOrder.phoneNumber}
                onChange={handleInputChange}
                required
              />
            </label>
            <label>
              Pin Code:
              <input
                type="text"
                name="pinCode"
                value={newGiftOrder.pinCode}
                onChange={handleInputChange}
                required
              />
            </label>
            <label>
              Invoice Number:
              <input
                type="text"
                name="invoiceNumber"
                value={newGiftOrder.invoiceNumber}
                onChange={handleInputChange}
                required
              />
            </label>
            <label>
              Total Credits:
              <input
                type="text"
                name="totalCredits"
                value={newGiftOrder.totalCredits}
                onChange={handleInputChange}
                required
              />
            </label>
            <label>
              Gifts:
              <textarea
                name="gifts"
                value={newGiftOrder.gifts}
                onChange={handleInputChange}
                required
                style={{ height: "80px" }}
              ></textarea>
            </label>
            <label>
              Details:
              <textarea
                name="details"
                value={newGiftOrder.details}
                onChange={handleInputChange}
                required
                style={{ height: "80px" }}
              ></textarea>
            </label>
            <button type="submit" disabled={loading}>
              Add Gift Order
            </button>
          </form>
        </div>
      </Modal>

      <Modal
        isOpen={passwordModalIsOpen}
        onRequestClose={() => setPasswordModalIsOpen(false)}
        className="modal"
        overlayClassName="modal-overlay"
        contentLabel="Password Modal"
        onOverlayClick={handleOverlayClick}
      >
        <div className="modal-header">
          <h2>Enter Password</h2>
          <button onClick={() => setPasswordModalIsOpen(false)}>Close</button>
        </div>
        <div className="modal-body">
          <label>
            Password:
            <input
              type="password"
              value={passwordInput}
              onChange={handlePasswordInputChange}
              minLength={4}
              maxLength={4}
              pattern="[0-9]{4}"
              required
            />
          </label>
          <button type="button" onClick={handlePasswordSubmit}>
            Submit
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default OrderGifts;
